import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useContent, useUi, useAuth } from '@/stores/RootStore';
import logoUrl from '@svg/evstech-logo-white.svg';
import TrialModal from '@/components/TrialModal';
import s from './Header.module.scss';

// 상단 nav — evs.com/as 디자인.
// 목업 실측(2026-07-31): 헤더 auto-hide 패턴(최상단 투명 → 아래 스크롤 숨김 → 위 스크롤 재등장 반투명).
// 서브메뉴(2026-08-01): evs.com/company 스타일 "풀폭 메가 패널" — 메인 메뉴 클릭 시 열림.
//   좌측 큰 시안 제목 + ↘, 구분선, 항목 리스트(항목 아래 divider), 우상단 X. Esc·바깥클릭·스크롤 시 닫힘.
//   ★항목(서브메뉴) 클릭 네비게이션은 아직 미구현(사용자 지시) — href='#', preventDefault.
const HIDE_AFTER = 160;
const DEADZONE = 4;
// [2026-09-09 사용자] 완성된 라우트 목록 — 메인 메뉴 '클릭' 시 이 페이지 '최상위'로 이동(호버는 서브메뉴).
//   서브메뉴 항목 클릭은 각 페이지 해당 섹션(#앵커)로. 미완성(목록 밖) 메뉴는 클릭 시 서브메뉴 토글로 폴백.
// 네비게이션 '이동 허용' 접두사 — 여기 없는 경로는 클릭해도 preventDefault만 되고 이동하지 않음(line 191·231).
//   [2026-10-08] 기획서 v7 B안 반영: 설치안내 재정의(/install/*)·환경 편입(/business/*) 추가.
//   누락 시 '설치안내' 메인/서브, 환경 서브의 'IoT 게이트웨이 제조·IoT 설치사업' 클릭이 죽는 버그 발생 → 재발 방지로 명시.
const NAV_READY = ['/products', '/safety', '/environment', '/install', '/business', '/guide', '/cases', '/news', '/support', '/company'];
const isReady = (to?: string) => !!to && NAV_READY.some((p) => to.startsWith(p));

const Header = observer(() => {
  const content = useContent();
  const ui = useUi();
  const auth = useAuth();
  const navigate = useNavigate();
  // [2026-10-11 사용자] 히어로 이미지가 없는 white 페이지(사이트맵 등)는 상단부터 헤더를 솔리드(흰 배경+다크 글자)로.
  //   투명 헤더는 다크 히어로 전제라, white 페이지에선 흰 NAV 글자가 안 보임. 해당 경로만 강제 솔리드.
  const loc = useLocation();
  const forceSolid = loc.pathname === '/sitemap';
  const lastY = useRef(0);
  const headerRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null); // 메가패널 전체(풀폭) — 호버 유지 판정 기준(innerRef는 좌측 박스라 우측 이동 시 오닫힘)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [trialOpen, setTrialOpen] = useState(false); // 체험하기(SaaS 데모) 모달
  // [2026-09-22 사용자] 메가패널 개편 — 기존 '풀폭·100vh 다크 슬래브'는 EVS처럼 항목이 적은 사이트에선 우측·하단
  //   빈 공간이 과함(캡처 근거). → '호버한 메뉴 바로 아래 컴팩트 드롭다운 카드'(각 서브메뉴만 배경 채움).
  //   anchorX = 호버/클릭된 메뉴 버튼의 좌측 x(뷰포트=헤더 기준, header inset left:0). 우측 메뉴는 화면 밖 넘침 방지 클램프.
  const [anchorX, setAnchorX] = useState(0);
  const CARD_W = 340; // 카드 대략 폭(넘침 클램프용). SCSS .megaPanel width와 맞춤.
  const anchorFrom = (el: HTMLElement) => {
    const left = el.getBoundingClientRect().left;
    setAnchorX(Math.max(12, Math.min(left, window.innerWidth - CARD_W - 24)));
  };

  // 내부 라우트(+해시) 이동: 메가패널 닫고 React Router로 이동. Company 페이지가 해시 섹션으로 부드럽게 스크롤.
  const goTo = (to: string) => { setOpenMenu(null); navigate(to); };

  // ── 서브메뉴 hover 열기(2026-09-08 사용자) — 클릭에 더해 마우스 롤오버로도 열림 ──
  //   메가패널은 풀폭·풀높이(≈100vh, header의 DOM 자식). 닫기는 클릭 토글·X·Esc·스크롤(기존)로 처리.
  //   ★hover-intent(90ms): nav 위를 '지나가기만' 할 때 풀스크린 패널이 번쩍 열리는 걸 방지 —
  //     닫힌 상태에선 90ms 머물러야 열리고, 이미 열려 있으면 다른 메뉴로 즉시 전환(표준 메가메뉴 UX).
  //   클릭은 토글 유지(hover로 열린 걸 클릭해 닫을 수 있음 = 실무적 대처).
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  //   suppressHover: 클릭으로 패널을 닫은 직후, 마우스가 아직 그 메뉴 위에 있어 hover가 즉시 재열지 못하게 억제.
  //     같은 메뉴에서 마우스가 벗어날 때(onMouseLeave) 해제 → 다시 hover하면 정상 열림.
  const suppressHover = useRef(false);
  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const cancelOpen = () => { if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; } };
  const hoverOpen = (i: number, hasChildren: boolean, el: HTMLElement) => {
    if (suppressHover.current) return;                                           // 방금 클릭으로 닫음 → 재열림 억제
    cancelClose(); cancelOpen();
    if (hasChildren) anchorFrom(el);                                             // 컴팩트 카드 위치 = 이 메뉴 아래
    const target = hasChildren ? i : null;
    if (openMenu !== null) setOpenMenu(target);                                   // 이미 열림 → 즉시 전환
    else if (hasChildren) openTimer.current = setTimeout(() => setOpenMenu(i), 90); // 닫힘 → intent 지연
  };
  // [2026-09-08 사용자] "서브메뉴가 늦게 사라짐" 수정 — 기존엔 mousemove마다 scheduleClose가 타이머를 리셋해서
  //   포인터가 밖에서 계속 움직이면 '멈출 때까지' 안 닫혔음. → 이미 예약돼 있으면 재설정 안 함(1회만). 지연 200→140ms.
  const scheduleClose = () => {
    cancelOpen();
    if (closeTimer.current) return; // 이미 닫힘 예약 중 → 리셋 금지(연속 mousemove로 지연 누적되던 문제)
    closeTimer.current = setTimeout(() => { closeTimer.current = null; setOpenMenu(null); }, 140);
  };
  useEffect(() => () => { cancelClose(); cancelOpen(); }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      ui.setScrolled(y > 10);
      if (y <= 10) ui.setHeaderHidden(false);
      else if (y > lastY.current + DEADZONE && y > HIDE_AFTER) {
        ui.setHeaderHidden(true);
        setOpenMenu(null); // 실제 아래 스크롤(헤더 숨김) 시에만 메가 패널 닫기(Lenis 미세 이벤트로 오닫힘 방지)
      } else if (y < lastY.current - DEADZONE) ui.setHeaderHidden(false);
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [ui]);

  // Esc / 바깥 클릭 시 닫기
  useEffect(() => {
    if (openMenu === null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenMenu(null); };
    const onDown = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [openMenu]);

  // hover-out 닫힘(2026-09-08 사용자) — 풀스크린 메가패널에선 React onMouseLeave가 백드롭 전이에서
  //   불안정하므로, 전역 mousemove로 포인터가 nav(상단메뉴)·메가패널(panelRef) 밖이면 닫기 예약.
  //   ★[2026-09-09 사용자 급버그] 기존엔 좌측 콘텐츠 박스(innerRef)만 유지영역이라, 우측 nav 라벨(예: 고객지원)
  //     아래로 내려가면 풀폭 패널의 '빈 우측'이 innerRef 밖이라 즉시 닫혔음. → 유지 기준을 패널 '전체'(panelRef)로.
  //   안이면 유지(cancelClose). 여유 12px. → "메뉴에서 마우스 떼면 닫힘"은 유지하되 패널 안이면 안 닫힘.
  useEffect(() => {
    if (openMenu === null) return;
    const within = (el: HTMLElement | null, x: number, y: number) => {
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return x >= r.left - 12 && x <= r.right + 12 && y >= r.top - 12 && y <= r.bottom + 12;
    };
    const onMove = (e: MouseEvent) => {
      if (within(navRef.current, e.clientX, e.clientY) || within(panelRef.current, e.clientX, e.clientY)) cancelClose();
      else scheduleClose();
    };
    document.addEventListener('mousemove', onMove, { passive: true });
    return () => document.removeEventListener('mousemove', onMove);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMenu]);

  const openItem = openMenu !== null ? content.nav[openMenu] : null;

  return (
    <header
      ref={headerRef}
      className={`${s.header} ${(ui.scrolled || forceSolid) ? s.solid : ''} ${ui.headerHidden ? s.hidden : ''} ${openMenu !== null ? s.menuOpen : ''}`}
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
    >
      <div className={s.inner}>
        <a href="/" className={s.brand} aria-label="EVS" onClick={(e) => { e.preventDefault(); goTo('/'); }}>
          <img src={logoUrl} alt="EVS" className={s.logo} />
        </a>

        <div className={s.util}>
          {/* [2026-09-10 P4] navUtil: '회원가입'은 /signup 이동(로그인 시 숨김). 나머지(사이트맵 등)는 기존대로. */}
          {content.navUtil.map((u) => {
            if (u === '회원가입') {
              if (auth.isAuthed) return null;
              return <a key={u} className={s.utilLink} style={{ cursor: 'pointer' }} onClick={() => goTo('/signup')}>{u}</a>;
            }
            // [2026-10-11 사용자] '사이트맵' → 신규 /sitemap 페이지 연결(구: 죽은 링크 href=#)
            if (u === '사이트맵') return <a key={u} className={s.utilLink} style={{ cursor: 'pointer' }} onClick={() => goTo('/sitemap')}>{u}</a>;
            return <a key={u} href="#" className={s.utilLink} onClick={(e) => e.preventDefault()}>{u}</a>;
          })}
          <button className={s.search} aria-label="검색">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" />
            </svg>
          </button>
          {/* [2026-09-10 P4] 로그인 상태 분기: 로그인 시 이름(→마이페이지)·관리자·로그아웃 / 비로그인 시 로그인 */}
          {auth.isAuthed ? (
            <>
              {/* [2026-09-13 사용자] 이름은 '누가 로그인했는지' 표시용(클릭 비활성). 계정은 별도 메뉴로: 일반='사용자 정보'(/mypage), 관리자='관리자'(/admin). */}
              <span className={s.login} style={{ cursor: 'default' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-6 8-6s8 2 8 6z" />
                </svg>
                {auth.user!.name}님
              </span>
              {auth.isAdmin
                ? <a className={s.utilLink} style={{ cursor: 'pointer' }} onClick={() => goTo('/admin')}>관리자</a>
                : <a className={s.utilLink} style={{ cursor: 'pointer' }} onClick={() => goTo('/mypage')}>사용자 정보</a>}
              <a className={s.utilLink} style={{ cursor: 'pointer' }} onClick={async () => { await auth.logout(); navigate('/'); }}>로그아웃</a>
            </>
          ) : (
            <a className={s.login} style={{ cursor: 'pointer' }} onClick={() => goTo('/login')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-6 8-6s8 2 8 6z" />
              </svg>
              로그인
            </a>
          )}
          {/* [2026-09-10 P4] 체험하기 = 로그인 필요(게이팅). 비로그인 시 로그인 페이지로, 로그인 시 SaaS 데모 모달. */}
          <button type="button" className={s.trial}
            onClick={() => { if (auth.isAuthed) setTrialOpen(true); else navigate('/login', { state: { from: '/' } }); }}>체험하기</button>
        </div>

        {/* hover-out 닫힘은 전역 mousemove 효과가 담당(navRef·innerRef 박스 기준). */}
        <nav className={s.nav} ref={navRef}>
          {content.nav.map((item, i) => (
            <button
              key={item.label}
              type="button"
              className={`${s.navLink} ${openMenu === i ? s.active : ''} ${item.dim ? s.dim : ''}`}
              onMouseEnter={(e) => hoverOpen(i, !!item.children, e.currentTarget)}
              onMouseLeave={() => { cancelOpen(); suppressHover.current = false; }}
              onClick={(e) => {
                cancelOpen();
                // [2026-09-09 사용자] 클릭 = 완성 페이지면 '최상위'로 이동(서브는 호버로 이미 노출). 미완성이면 서브 토글 폴백.
                if (isReady(item.to)) { setOpenMenu(null); goTo(item.to!); return; }
                if (!item.children) { setOpenMenu(null); return; }
                if (openMenu === i) { setOpenMenu(null); suppressHover.current = true; }
                else { anchorFrom(e.currentTarget); setOpenMenu(i); } // 클릭 열기도 카드 위치 앵커
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      {/* 메가 서브메뉴 패널 (evs.com/company 스타일) */}
      {openItem?.children && (
        <div className={s.megaPanel} ref={panelRef} style={{ left: anchorX }}>
          <button className={s.megaClose} onClick={() => setOpenMenu(null)} aria-label="닫기">
            {/* 닫기 X — 사용자 지시(2026-08-01) 더 크게: 24→34px. evs.com 닫기 버튼(≈57px 박스) 존재감에 맞춤. */}
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
          {/* 서브메뉴 내용 박스(innerRef) — 전역 mousemove 효과가 이 박스 안이면 열림 유지 */}
          <div className={s.megaInner} ref={innerRef}>
            <div className={s.megaTitle}>
              <span>{openItem.label}</span>
              <svg className={s.megaArrow} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 8l9 9M17 17V9M17 17H9" />
              </svg>
            </div>
            <div className={s.megaDivider} />
            <ul className={s.megaList}>
              {openItem.children.map((c) => {
                // [2026-09-08 사용자] 서브메뉴 클릭은 원칙적으로 데모 차단. 단 완성된 3 기본 페이지(제품/안전/환경)는
                //   이동 허용 — 제품(카탈로그 ?type= 필터) · 안전/환경(도메인 페이지 #앵커 섹션). 그 외 미완성 메뉴는 차단.
                //   재개 확대: 아래 allowNav 접두 목록에 완성된 라우트를 추가하면 해당 항목이 이동함.
                const allowNav = isReady(c.to);
                return (
                  <li key={c.label} className={c.sep ? s.megaSep : undefined}>
                    <a
                      href={c.to ?? c.href}
                      onClick={(e) => { e.preventDefault(); if (allowNav) goTo(c.to!); }}
                    >
                      {/* 도메인 구분자(dot) — 절제된 색 표현: 왼쪽 작은 점 하나(안전=시안/환경=그린). 배경·글자색 불변.
                          space-between 레이아웃이므로 dot+라벨을 좌측 그룹으로 묶어 함께 왼쪽 정렬. */}
                      <span className={s.megaItemLeft}>
                        {c.dot && <i className={`${s.megaDot} ${c.dot === 'safety' ? s.dotSafety : s.dotEnv}`} aria-hidden="true" />}
                        <span>{c.label}</span>
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
      {/* 체험하기(SaaS 데모) 임시계정 발급 폼 — 웹사이트 전용(대시보드 미수정) */}
      <TrialModal open={trialOpen} onClose={() => setTrialOpen(false)} />
    </header>
  );
});

export default Header;
